// Nouns — gender, articles, plurals, diminutives, plus a starter vocabulary
// table with guessed gender. Ported from the legacy views.nouns; VOCABULARY is
// imported directly (old shape: { pt, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

const FEM_OVERRIDES = new Set([
  "casa", "água", "comida", "família", "cidade", "vida", "noite", "semana", "hora",
]);
const MASC_OVERRIDES = new Set([
  "dia", "mapa", "problema", "tema", "sistema", "clima", "idioma",
]);

function guessGender(w) {
  if (FEM_OVERRIDES.has(w)) return { g: "f", note: "(a)" };
  if (MASC_OVERRIDES.has(w)) return { g: "m", note: "(o — exception)" };
  if (
    w.endsWith("ção") || w.endsWith("são") || w.endsWith("dade") ||
    w.endsWith("gem") || w.endsWith("ice") || w.endsWith("a")
  )
    return { g: "f", note: "(a)" };
  if (
    w.endsWith("o") || w.endsWith("or") || w.endsWith("ema") ||
    w.endsWith("oma") || w.endsWith("s")
  )
    return { g: "m", note: "(o)" };
  return { g: "?", note: "(check)" };
}

export function nounsView(app) {
  const nouns = VOCABULARY.filter((v) => v.cat === "noun");

  const vocabTable = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Starter Noun Vocabulary</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Portuguese</th>
              <th class="py-2 px-3">Gender</th>
              <th class="py-2 px-3">English</th>
            </tr>
          </thead>
          <tbody>
            ${nouns
              .map((n) => {
                const { g, note } = guessGender(n.pt);
                const color = g === "f" ? "text-pink-600" : g === "m" ? "text-blue-600" : "text-sage-500";
                return `<tr class="border-b border-sage-50 last:border-0">
                <td class="py-2 pr-3 font-medium text-sage-800">${n.pt}</td>
                <td class="py-2 px-3 ${color} font-semibold">${note}</td>
                <td class="py-2 px-3 text-sage-700">${n.en}</td>
              </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Nouns",
    "Portuguese nouns have grammatical gender (masculine or feminine) and number (singular or plural). The gender of a noun controls the form of articles and adjectives that go with it.",
    [
      {
        title: "1. Gender — Masculine vs Feminine",
        body: `
          <p class="text-sage-700 mb-3">Most nouns ending in <b>-o</b> are masculine; most ending in <b>-a</b> are feminine. But there are reliable extra clues and a small set of exceptions worth memorising.</p>
          <ul class="text-sage-700 text-sm space-y-1 mb-3 list-disc pl-5">
            <li><b>Masculine endings:</b> -o, -or, -ema / -oma (Greek origin). <i>o livro, o calor, o problema, o sistema.</i></li>
            <li><b>Feminine endings:</b> -a, -ção, -são, -dade, -gem. <i>a casa, a canção, a cidade, a liberdade, a viagem.</i></li>
            <li><b>Common exceptions:</b> <i>o dia, o mapa, o problema, o tema, o sistema, o idioma</i> (masculine despite ending in -a); <i>a mão, a foto, a moto</i> (feminine despite ending in -o).</li>
            <li><b>Same word, both genders:</b> <i>o/a estudante, o/a artista</i> — gender depends on the person.</li>
          </ul>
        `,
      },
      {
        title: "2. Articles — o / a / um / uma",
        body: `
          <p class="text-sage-700 mb-3">Articles always agree with the noun in gender and number.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3"></th><th class="py-2 px-3">Masculine</th><th class="py-2 px-3">Feminine</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite singular</td><td class="py-2 px-3"><b>o</b> livro</td><td class="py-2 px-3"><b>a</b> casa</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite plural</td><td class="py-2 px-3"><b>os</b> livros</td><td class="py-2 px-3"><b>as</b> casas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Indefinite singular</td><td class="py-2 px-3"><b>um</b> livro</td><td class="py-2 px-3"><b>uma</b> casa</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Indefinite plural</td><td class="py-2 px-3"><b>uns</b> livros</td><td class="py-2 px-3"><b>umas</b> casas</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Heads-up:</b> the article usually fuses with a preceding preposition — <i>de + o = <b>do</b></i>, <i>em + a = <b>na</b></i>, <i>a + o = <b>ao</b></i>, <i>por + os = <b>pelos</b></i>. So "the boy's book" is <i>o livro <b>do</b> rapaz</i>.</p>
        `,
      },
      {
        title: "3. Plurals",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Ends in a vowel → add <b>-s</b>: <i>casa → casas, livro → livros</i>.</li>
            <li>Ends in <b>-r, -z</b> or a stressed <b>-s</b> → add <b>-es</b>: <i>mulher → mulheres, luz → luzes, país → países</i>.</li>
            <li>Ends in <b>-m</b> → change to <b>-ns</b>: <i>homem → homens, jardim → jardins</i>.</li>
            <li>Ends in <b>-ão</b> → usually <b>-ões</b>, sometimes -ães / -ãos: <i>lição → lições, pão → pães, mão → mãos</i>.</li>
            <li>Ends in <b>-l</b> → drop the -l and add <b>-is</b>: <i>animal → animais, papel → papéis, hotel → hotéis</i>.</li>
          </ul>
        `,
      },
      {
        title: "4. Diminutives & Augmentatives",
        body: `
          <p class="text-sage-700 mb-2">Portuguese loves modifying nouns with suffixes for size, affection, or emphasis.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>-inho / -inha</b> (small, cute): <i>gato → gatinho, casa → casinha</i>.</li>
            <li><b>-zinho / -zinha</b> (after a nasal or stressed vowel): <i>cão → cãozinho, café → cafezinho</i>.</li>
            <li><b>-ão</b> (big): <i>carro → carrão</i> (big car), <i>casa → casarão</i>.</li>
            <li><b>-aço / -aça</b> (great, impressive): <i>golo → golaço</i> (a great goal), <i>amigo → amigaço</i> (a great friend).</li>
          </ul>
        `,
      },
    ],
    "",
    vocabTable
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
